import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { CourseSection, CourseSectionRepoItem } from '@/types/api/course.types';
import {
  ClipboardList,
  EllipsisVertical,
  FileText,
  Link2,
  Paperclip,
  Play,
  PlayCircle,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { EditableRichInfoCard, EditableTextInfoCard } from './editable-info-fields';
import { getVideoEmbedUrl, getVideoThumbnailUrl } from './video-utils';
import CourseAssignmentsContent from './course-assignments-content';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePatchCourseSectionMutation } from '@/state/course';
import { useAddPublicImageResourceMutation } from '@/state/user/client-api';
import { useTenant } from '@/hooks';
import { toast } from 'sonner';
import { useCallback } from 'react';

interface ChapterTabsContentProps {
  selectedSection: CourseSection | null;
  courseStructureId: number;
  currentChapterTab: string;
  onChapterTabChange: (value: string) => void;
  sectionVideos: CourseSectionRepoItem[];
  sectionDocuments: CourseSectionRepoItem[];
  canEdit: boolean;
  onOpenAddVideoModal: () => void;
  onOpenEditVideoModal: (video: CourseSectionRepoItem) => void;
  onSetVideoToDelete: (video: CourseSectionRepoItem) => void;
  isPatchingLmsRepo: boolean;
  isDeletingLmsRepo: boolean;
  playingVideoId: number | null;
  onSetPlayingVideoId: (id: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onUploadChapterFile: (file: File) => void;
  onSetDocumentToDelete: (doc: CourseSectionRepoItem) => void;
  isRepoLoading: boolean;
}

const renderEmptyState = (title: string, description: string) => (
  <div className="h-full min-h-[360px] rounded-xl border border-dashed bg-card p-6 shadow-sm">
    <div className="flex h-full items-center justify-center">
      <div className="max-w-md text-center">
        <p className="text-base font-semibold text-card-foreground">{title}</p>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  </div>
);

const ChapterTabsContent = ({
  selectedSection,
  courseStructureId,
  currentChapterTab,
  onChapterTabChange,
  sectionVideos,
  sectionDocuments,
  canEdit,
  onOpenAddVideoModal,
  onOpenEditVideoModal,
  onSetVideoToDelete,
  isPatchingLmsRepo,
  isDeletingLmsRepo,
  playingVideoId,
  onSetPlayingVideoId,
  fileInputRef,
  onUploadChapterFile,
  onSetDocumentToDelete,
  isRepoLoading,
}: ChapterTabsContentProps) => {
  const { tenant } = useTenant();
  const [patchCourseSection] = usePatchCourseSectionMutation();
  const [addPublicImageResource] = useAddPublicImageResourceMutation();

  const handleUploadFile = useCallback(
    async (file: File): Promise<string> => {
      try {
        const formData = new FormData();
        formData.append('image_path', file);
        const data = await addPublicImageResource({ data: formData }).unwrap();
        const src = data?.image_path || data?.url || data?.file;
        if (src) return src;
        throw new Error('No image path returned from upload');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to upload image.');
        return URL.createObjectURL(file);
      }
    },
    [addPublicImageResource]
  );

  const patchSectionField = useCallback(
    async (fieldName: 'section_name' | 'section_brief', value: string) => {
      if (!tenant || !selectedSection?.id) return;
      const formData = new FormData();
      formData.append(fieldName, value);
      await patchCourseSection({
        tenant,
        sectionId: selectedSection.id,
        data: formData,
      }).unwrap();
      toast.success('Updated successfully');
    },
    [patchCourseSection, selectedSection?.id, tenant]
  );

  return (
    <Tabs
      key={selectedSection?.id}
      value={currentChapterTab}
      onValueChange={onChapterTabChange}
      className="w-full min-h-[60vh] flex flex-col"
    >
      <TabsList className="h-auto w-full lg:w-auto shrink-0 justify-start overflow-x-auto gap-2 rounded-xl border bg-card p-2 shadow-sm">
        <TabsTrigger className="!flex-none gap-2 px-3 sm:px-4" value="chapter-details">
          <FileText className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">Chapter info</span>
        </TabsTrigger>
        <TabsTrigger className="!flex-none gap-2 px-3 sm:px-4" value="videos">
          <PlayCircle className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">Videos</span>
        </TabsTrigger>
        <TabsTrigger className="!flex-none gap-2 px-3 sm:px-4" value="attachments">
          <Paperclip className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">Attachments</span>
        </TabsTrigger>
        <TabsTrigger className="!flex-none gap-2 px-3 sm:px-4" value="assignments">
          <ClipboardList className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">Assignments</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="chapter-details" className="mt-3 flex-1">
        {selectedSection ? (
          <div className="space-y-4">
            <EditableTextInfoCard
              title="Chapter name"
              subtitle="Chapter title shown across this course"
              icon={<FileText className="h-4 w-4" />}
              value={selectedSection.section_name}
              placeholder="Add Chapter name..."
              canEdit={canEdit}
              onSave={async (nextValue) => {
                await patchSectionField('section_name', nextValue);
              }}
            />
            <EditableRichInfoCard
              title="Chapter details"
              subtitle="Brief information and context for this chapter"
              icon={<ClipboardList className="h-4 w-4" />}
              value={selectedSection.section_brief || selectedSection.section_summary}
              placeholder="Add chapter details..."
              canEdit={canEdit}
              onUploadFile={handleUploadFile}
              onSave={async (nextValue) => {
                await patchSectionField('section_brief', nextValue);
              }}
            />
          </div>
        ) : (
          renderEmptyState('No chapter selected', 'Select a chapter to view details.')
        )}
      </TabsContent>
      <TabsContent value="videos" className="mt-3 flex-1">
        <div className="h-full min-h-[420px] rounded-xl border bg-card p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {canEdit && (
              <button
                type="button"
                onClick={onOpenAddVideoModal}
                className="min-h-[160px] rounded-xl border-2 border-dashed border-muted-foreground/30 hover:border-primary/40 bg-background/70 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground"
              >
                <Plus className="h-5 w-5" />
                <span className="font-medium">Add video link</span>
              </button>
            )}

            {sectionVideos.length === 0 && (
              <div className="md:col-span-2 xl:col-span-3">
                {renderEmptyState(
                  'No videos added',
                  canEdit
                    ? 'No video links yet. Use "Add video link" to add the first one.'
                    : 'Video links for this chapter have not been added yet.'
                )}
              </div>
            )}

            {sectionVideos.map((video) => {
              const embedUrl = getVideoEmbedUrl(video.content);
              const thumbnailUrl = getVideoThumbnailUrl(video.content);

              return (
                <div key={video.id} className="rounded-xl border bg-background p-3 shadow-sm">
                  <div className="relative overflow-hidden rounded-lg border bg-muted">
                    {playingVideoId === video.id && embedUrl ? (
                      <iframe
                        src={embedUrl}
                        className="aspect-video w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        title={video.name}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (embedUrl) {
                            onSetPlayingVideoId(video.id);
                          } else {
                            window.open(video.content, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        className="group relative block aspect-video w-full text-left"
                      >
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt={video.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900" />
                        )}
                        <div className="absolute inset-0 bg-black/35 transition-colors group-hover:bg-black/45" />
                        <p className="absolute left-3 top-2 right-12 line-clamp-1 text-white text-lg font-semibold drop-shadow-sm">
                          {video.name}
                        </p>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-xl ring-1 ring-black/10">
                            <Play className="h-7 w-7 fill-current ml-0.5" />
                          </span>
                        </div>
                      </button>
                    )}

                    {canEdit && (
                      <div className="absolute right-2 top-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-background/90 backdrop-blur hover:bg-background"
                            >
                              <EllipsisVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => onOpenEditVideoModal(video)}
                              disabled={isPatchingLmsRepo}
                            >
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => onSetVideoToDelete(video)}
                              disabled={isDeletingLmsRepo}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 px-1">
                    <p className="text-sm font-medium line-clamp-1">{video.name}</p>
                    <a
                      href={video.content}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      Open source link
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </TabsContent>
      <TabsContent value="attachments" className="mt-3 flex-1">
        <div className="h-full min-h-[420px] rounded-xl border bg-card p-6 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {canEdit && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) onUploadChapterFile(file);
                }}
                className="min-h-[170px] rounded-xl border-2 border-dashed border-muted-foreground/30 hover:border-primary/40 bg-background/70 transition-colors px-4 py-5 text-center"
              >
                <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                  <Upload className="h-5 w-5" />
                  <p className="font-medium">Drag and drop files here</p>
                  <p className="text-sm">Supported: PDF, PPT, DOC, DOCX, XLSX, PNG, JPEG</p>
                </div>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.ppt,.pptx,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onUploadChapterFile(file);
                e.target.value = '';
              }}
            />

            {sectionDocuments.map((doc) => (
              <div
                key={doc.id}
                className="group relative rounded-xl border bg-background shadow-sm overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => {
                    if (!doc.document) return;
                    window.open(doc.document, '_blank', 'noopener,noreferrer');
                  }}
                  className="block w-full text-left"
                >
                  <div className="aspect-[1.8/1] bg-muted flex items-center justify-center">
                    {doc.document ? (
                      <img
                        src={doc.document}
                        alt={doc.name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <Paperclip className="h-8 w-8 text-muted-foreground/80" />
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium truncate">{doc.name}</p>
                  </div>
                </button>

                {canEdit && (
                  <button
                    type="button"
                    onClick={() => onSetDocumentToDelete(doc)}
                    className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-destructive shadow-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                    title="Delete document"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}

            {!isRepoLoading && sectionDocuments.length === 0 && (
              <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
                {renderEmptyState(
                  'No attachments yet',
                  canEdit
                    ? 'Upload a document to get started.'
                    : 'Attachments for this chapter will appear here once added.'
                )}
              </div>
            )}
          </div>
        </div>
      </TabsContent>
      <TabsContent value="assignments" className="mt-3 flex-1">
        <CourseAssignmentsContent
          canEdit={canEdit}
          tenant={tenant || ''}
          courseId={selectedSection?.course ?? 0}
          courseStructureId={courseStructureId}
          courseSectionId={selectedSection?.id ?? null}
          sectionName={selectedSection?.section_name}
          emptyText="Assignments for this chapter will appear here once they are added."
          addLabel="Add assignment"
        />
      </TabsContent>
    </Tabs>
  );
};

export default ChapterTabsContent;
